import {announce} from '@github-ui/aria-live'
import {Banner} from '@primer/react'
import {useEffect} from 'react'

import type {ApiError} from '../platform/api-error'
import {useProjectRouteParams} from '../router/use-project-route-params'
import {PROJECT_SETTINGS_ROUTE} from '../routes'
import {AddIssueFieldsDialogBannerResources} from '../strings'
import {
  getValidationResult,
  isServerFieldLimitError,
  MAX_FIELDS_PER_IMPORT,
  MAX_FIELDS_PER_PROJECT,
  ValidationResult,
} from './add-issue-fields-dialog-validation'

type BannerConfig = {
  variant: 'critical' | 'warning'
  title: string
  hideTitle: boolean
  description: string
  actionsLayout?: 'inline' | 'stacked'
  primaryAction?: {
    label: string
    onClick?: () => void
    href?: string
  }
}

function getBannerConfig(
  hasAttemptedSubmit: boolean,
  projectCount: number,
  selectedCount: number,
  importError?: ApiError | null,
  onRetry?: () => void,
  projectSettingsPath?: string,
): BannerConfig | null {
  if (importError) {
    if (isServerFieldLimitError(importError)) {
      return {
        variant: 'warning',
        hideTitle: false,
        title: AddIssueFieldsDialogBannerResources.fieldLimitReachedTitle,
        description: AddIssueFieldsDialogBannerResources.fieldLimitReachedDescription(projectCount),
        actionsLayout: 'inline',
        primaryAction: {
          label: AddIssueFieldsDialogBannerResources.manageFieldsAction,
          href: projectSettingsPath,
        },
      }
    }

    return {
      variant: 'critical',
      title: AddIssueFieldsDialogBannerResources.errorAddingFields,
      hideTitle: true,
      description: AddIssueFieldsDialogBannerResources.errorAddingFields,
      actionsLayout: 'inline',
      primaryAction: {
        label: AddIssueFieldsDialogBannerResources.tryAgainAction,
        onClick: onRetry,
      },
    }
  }

  const validationResult = getValidationResult(selectedCount, projectCount)

  if (validationResult === ValidationResult.FieldLimitReached) {
    return {
      variant: 'warning',
      title: AddIssueFieldsDialogBannerResources.fieldLimitReachedTitle,
      hideTitle: false,
      description: AddIssueFieldsDialogBannerResources.fieldLimitReachedDescription(projectCount),
      actionsLayout: 'inline',
      primaryAction: {
        label: AddIssueFieldsDialogBannerResources.manageFieldsAction,
        href: projectSettingsPath,
      },
    }
  }

  if (hasAttemptedSubmit && validationResult === ValidationResult.NoFieldsSelected) {
    return {
      variant: 'warning',
      title: AddIssueFieldsDialogBannerResources.noFieldsSelectedTitle,
      hideTitle: false,
      description: AddIssueFieldsDialogBannerResources.noFieldsSelectedDescription,
    }
  }

  if (validationResult === ValidationResult.WouldExceedProjectLimit) {
    return {
      variant: 'warning',
      title: AddIssueFieldsDialogBannerResources.wouldExceedFieldLimitTitle,
      hideTitle: false,
      description: AddIssueFieldsDialogBannerResources.wouldExceedFieldLimitDescription(
        MAX_FIELDS_PER_PROJECT,
        projectCount,
      ),
      actionsLayout: 'inline',
      primaryAction: {
        label: AddIssueFieldsDialogBannerResources.manageFieldsAction,
        href: projectSettingsPath,
      },
    }
  }

  if (validationResult === ValidationResult.ExceedsImportLimit) {
    return {
      variant: 'warning',
      title: AddIssueFieldsDialogBannerResources.tooManyFieldsSelectedTitle,
      hideTitle: false,
      description: AddIssueFieldsDialogBannerResources.tooManyFieldsSelectedDescription(MAX_FIELDS_PER_IMPORT),
    }
  }

  return null
}

type Props = {
  projectCount: number
  selectedCount: number
  hasAttemptedSubmit: boolean
  importError?: ApiError | null
  onRetry?: () => void
}

export function AddIssueFieldsDialogBanner({
  projectCount,
  selectedCount,
  hasAttemptedSubmit,
  importError,
  onRetry,
}: Props) {
  const projectRouteParams = useProjectRouteParams()
  const projectSettingsPath = PROJECT_SETTINGS_ROUTE.generatePath(projectRouteParams)

  const bannerConfig = getBannerConfig(
    hasAttemptedSubmit,
    projectCount,
    selectedCount,
    importError,
    onRetry,
    projectSettingsPath,
  )

  let announcement: string | null = null
  if (bannerConfig) {
    if (bannerConfig.hideTitle) {
      announcement = bannerConfig.description ?? null
    } else if (bannerConfig.description) {
      announcement = `${bannerConfig.title}. ${bannerConfig.description}`
    } else {
      announcement = bannerConfig.title
    }
  }

  useEffect(() => {
    if (announcement) {
      announce(announcement)
    }
  }, [announcement])

  if (!bannerConfig) {
    return null
  }

  return (
    <Banner
      variant={bannerConfig.variant}
      role={bannerConfig.variant === 'critical' ? 'alert' : undefined}
      className="m-2"
      title={bannerConfig.title}
      hideTitle={bannerConfig.hideTitle}
      description={bannerConfig.description}
      actionsLayout={bannerConfig.actionsLayout}
      primaryAction={
        bannerConfig.primaryAction &&
        (bannerConfig.primaryAction.href ? (
          <Banner.PrimaryAction as="a" href={bannerConfig.primaryAction.href}>
            {bannerConfig.primaryAction.label}
          </Banner.PrimaryAction>
        ) : (
          <Banner.PrimaryAction onClick={bannerConfig.primaryAction.onClick}>
            {bannerConfig.primaryAction.label}
          </Banner.PrimaryAction>
        ))
      }
    />
  )
}

try{ AddIssueFieldsDialogBanner.displayName ||= 'AddIssueFieldsDialogBanner' } catch {}